import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import {
  ArrowLeft,
  Upload,
  Camera,
  Loader2,
  Video,
  Sparkles,
  Play,
  Clock
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const videoTypes = [
  { id: "sales", name: "Sales Video", icon: "💰", desc: "Persuasive product showcase" },
  { id: "marketing", name: "Marketing", icon: "📢", desc: "Brand awareness" },
  { id: "explainer", name: "Explainer", icon: "📚", desc: "How it works" }
];

const tones = [
  { id: "professional", name: "Professional", icon: "👔" },
  { id: "casual", name: "Casual", icon: "😊" },
  { id: "energetic", name: "Energetic", icon: "⚡" },
  { id: "serious", name: "Serious", icon: "🎯" }
];

const durations = [
  { value: 15, label: "15 sec", icon: "⚡" },
  { value: 30, label: "30 sec", icon: "🎬" },
  { value: 60, label: "1 min", icon: "📽️" }
];

export default function PhotoToVideo() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [productName, setProductName] = useState("");
  const [videoType, setVideoType] = useState("sales");
  const [tone, setTone] = useState("professional");
  const [duration, setDuration] = useState(30);
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null);

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onload = (e) => setImagePreview(e.target.result);
      reader.readAsDataURL(file);
    }
  };

  const handleGenerate = async () => {
    if (!imageFile) {
      toast.error("Please upload a product photo");
      return;
    }
    if (!productName.trim()) {
      toast.error("Please enter a product name");
      return;
    }

    setGenerating(true);
    setResult(null);

    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        const base64 = e.target.result.split(",")[1];
        const token = localStorage.getItem("token");
        
        const response = await axios.post(
          `${API}/photo-to-video`,
          {
            image_base64: base64,
            product_name: productName,
            video_type: videoType,
            tone: tone,
            duration: duration
          },
          {
            headers: token ? { Authorization: `Bearer ${token}` } : {}
          }
        );
        
        setResult(response.data);
        toast.success("Script generated! Continue in the studio to create your video.");
        setGenerating(false);
      };
      reader.readAsDataURL(imageFile);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to generate video");
      setGenerating(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-40 glass border-b border-border">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={() => navigate("/dashboard")}
            className="text-text-secondary hover:text-text-primary"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <span className="font-heading font-bold text-text-primary">Photo to Video</span>
          <div className="w-20" />
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8">
        <div className="space-y-8">
          {/* Header */}
          <div className="text-center">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-primary to-accent-neon flex items-center justify-center mx-auto mb-4">
              <Camera className="w-8 h-8 text-white" />
            </div>
            <h1 className="font-heading font-bold text-2xl text-text-primary mb-2">
              Turn Any Photo Into a Sales Video
            </h1>
            <p className="text-text-secondary text-sm max-w-md mx-auto">
              Upload a product photo (like a Rolex watch) and our AI will create a compelling sales video with script and voiceover
            </p>
          </div>

          {!result ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-6"
            >
              {/* Image Upload */}
              <Card className="bg-background-paper border-border">
                <CardContent className="p-6">
                  <Label className="text-text-primary mb-3 block">Product Photo</Label>
                  <div
                    onClick={() => fileInputRef.current?.click()}
                    className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all ${
                      imagePreview ? "border-primary bg-primary/5" : "border-border hover:border-primary/50"
                    }`}
                  >
                    {imagePreview ? (
                      <div className="space-y-3">
                        <img
                          src={imagePreview}
                          alt="Preview"
                          className="max-h-64 mx-auto rounded-lg shadow-lg"
                        />
                        <p className="text-sm text-text-muted">Click to change photo</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        <Upload className="w-12 h-12 text-text-muted mx-auto" />
                        <p className="text-text-primary">Click to upload product photo</p>
                        <p className="text-xs text-text-muted">PNG, JPG up to 10MB</p>
                        <p className="text-xs text-primary">e.g., Rolex watch, Nike shoes, iPhone</p>
                      </div>
                    )}
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleImageSelect}
                    className="hidden"
                  />
                </CardContent>
              </Card>

              {/* Product Name */}
              <Card className="bg-background-paper border-border">
                <CardContent className="p-6">
                  <Label className="text-text-primary mb-2 block">Product Name</Label>
                  <Input
                    value={productName}
                    onChange={(e) => setProductName(e.target.value)}
                    placeholder="e.g., Rolex Submariner, Nike Air Max 90"
                    className="bg-background border-border text-text-primary"
                  />
                </CardContent>
              </Card>

              {/* Video Type */}
              <Card className="bg-background-paper border-border">
                <CardContent className="p-6">
                  <Label className="text-text-primary mb-3 block">Video Type</Label>
                  <div className="grid grid-cols-3 gap-3">
                    {videoTypes.map((type) => (
                      <div
                        key={type.id}
                        onClick={() => setVideoType(type.id)}
                        className={`p-4 rounded-lg border cursor-pointer transition-all text-center ${
                          videoType === type.id
                            ? "bg-primary/10 border-primary"
                            : "bg-background border-border hover:border-primary/50"
                        }`}
                      >
                        <span className="text-2xl">{type.icon}</span>
                        <div className="text-sm font-medium text-text-primary mt-2">{type.name}</div>
                        <div className="text-xs text-text-muted">{type.desc}</div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Tone & Duration */}
              <div className="grid grid-cols-2 gap-4">
                <Card className="bg-background-paper border-border">
                  <CardContent className="p-6">
                    <Label className="text-text-primary mb-3 block">Tone</Label>
                    <div className="grid grid-cols-2 gap-2">
                      {tones.map((t) => (
                        <div
                          key={t.id}
                          onClick={() => setTone(t.id)}
                          className={`p-3 rounded-lg border cursor-pointer transition-all text-center ${
                            tone === t.id
                              ? "bg-primary/10 border-primary"
                              : "bg-background border-border hover:border-primary/50"
                          }`}
                        >
                          <span>{t.icon}</span>
                          <div className="text-xs text-text-primary mt-1">{t.name}</div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-background-paper border-border">
                  <CardContent className="p-6">
                    <Label className="text-text-primary mb-3 block">Duration</Label>
                    <div className="grid grid-cols-3 gap-2">
                      {durations.map((d) => (
                        <div
                          key={d.value}
                          onClick={() => setDuration(d.value)}
                          className={`p-3 rounded-lg border cursor-pointer transition-all text-center ${
                            duration === d.value
                              ? "bg-primary/10 border-primary"
                              : "bg-background border-border hover:border-primary/50"
                          }`}
                        >
                          <span>{d.icon}</span>
                          <div className="text-xs text-text-primary mt-1">{d.label}</div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Generate Button */}
              <Button
                onClick={handleGenerate}
                disabled={generating || !imageFile || !productName.trim()}
                className="w-full bg-gradient-to-r from-primary to-accent-neon text-white py-6 text-lg"
              >
                {generating ? (
                  <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Generating Script...</>
                ) : (
                  <><Sparkles className="w-5 h-5 mr-2" />Generate Sales Video</>
                )}
              </Button>
            </motion.div>
          ) : (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="space-y-6"
            >
              {/* Success */}
              <Card className="bg-accent-success/10 border-accent-success">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 rounded-full bg-accent-success/20 flex items-center justify-center">
                      <Sparkles className="w-6 h-6 text-accent-success" />
                    </div>
                    <div>
                      <h3 className="font-bold text-text-primary">Script Generated!</h3>
                      <p className="text-sm text-text-muted">Continue to the studio to create your video</p>
                    </div>
                  </div>

                  {/* Script Preview */}
                  <div className="p-4 rounded-lg bg-background border border-border mb-4">
                    <Label className="text-text-muted text-xs mb-2 block">Generated Script</Label>
                    <p className="text-text-primary text-sm whitespace-pre-wrap">
                      {result.script}
                    </p>
                  </div>

                  {/* Source Image */}
                  {result.source_image && (
                    <div className="mb-4">
                      <Label className="text-text-muted text-xs mb-2 block">Source Image</Label>
                      <img
                        src={`${process.env.REACT_APP_BACKEND_URL}${result.source_image}`}
                        alt="Source"
                        className="max-h-32 rounded-lg"
                      />
                    </div>
                  )}

                  <Button
                    onClick={() => navigate(`/studio/${result.project_id}`)}
                    className="w-full bg-primary hover:bg-primary-hover text-white"
                  >
                    <Video className="w-5 h-5 mr-2" />
                    Continue to Studio
                  </Button>
                </CardContent>
              </Card>

              <Button
                variant="outline"
                onClick={() => {
                  setResult(null);
                  setImageFile(null);
                  setImagePreview(null);
                  setProductName("");
                }}
                className="w-full border-border"
              >
                Create Another Video
              </Button>
            </motion.div>
          )}
        </div>
      </main>
    </div>
  );
}
